/**
 * CR-5563: the lower breakdown cards of the supplier Performance tab.
 *
 * Split out of PerformanceTab.tsx, which the period control pushed past the
 * repo's 500-line limit. Pure presentation: every figure comes from the
 * windowed profile the tab resolved, and every handler is the tab's.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { SupplierProfile } from '../../lib/api';
import { StatRow, formatNumber } from './performanceFormatting';

interface PerformanceBreakdownCardsProps {
  supplier: SupplierProfile;
  qualityLabel: string;
  /** null = N/A: nothing closed or overdue to score (METRIC-DEFINITIONS Q13). */
  otifPercent: number | null;
  otdPercent: number;
  /** Dims the grid while the next period's figures are loading. */
  dimmed: boolean;
  onNavigateToNCRs: (liability?: '1' | '2') => void;
  onNavigateToPOLines: () => void;
  onNavigateToPOHeaders: (statusTab?: 'all' | 'closed') => void;
  onOpenOtdDrilldown: (tab?: 'on_time' | 'early' | 'late' | 'total') => void;
  onOpenOtifDrilldown: (tab?: 'failed' | 'arrears' | 'positive' | 'total') => void;
}

export function PerformanceBreakdownCards({
  supplier,
  qualityLabel,
  otifPercent,
  otdPercent,
  dimmed,
  onNavigateToNCRs,
  onNavigateToPOLines,
  onNavigateToPOHeaders,
  onOpenOtdDrilldown,
  onOpenOtifDrilldown,
}: PerformanceBreakdownCardsProps) {
  return (
    <div className={`grid gap-4 md:grid-cols-2 ${dimmed ? 'opacity-60 transition-opacity' : ''}`}>
      {/* Quality Metrics - CR-5765: Clickable rows */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            Quality Metrics
            <span className="text-xs text-muted-foreground font-normal ml-auto">Click values to drill down</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-border">
            <StatRow
              label={`Total ${qualityLabel}`}
              value={formatNumber(supplier.total_ppm, 0)}
              onClick={() => onNavigateToNCRs()}
            />
            <StatRow
              label={`Supplier Liability ${qualityLabel}`}
              value={formatNumber(supplier.supplier_liability_ppm, 0)}
              onClick={() => onNavigateToNCRs('1')}
            />
            <StatRow
              label={`Customer Liability ${qualityLabel}`}
              value={formatNumber(supplier.customer_liability_ppm, 0)}
              onClick={() => onNavigateToNCRs('2')}
            />
            <StatRow
              label="NCR Count"
              value={formatNumber(supplier.ncr_count)}
              onClick={() => onNavigateToNCRs()}
            />
            <StatRow
              label="Qty Received"
              value={formatNumber(supplier.qty_received)}
            />
            <StatRow
              label="Reject Qty"
              value={formatNumber(supplier.reject_qty)}
              onClick={() => onNavigateToNCRs()}
            />
            <StatRow
              label={`${qualityLabel} Mapped Score`}
              value={supplier.ppm_mapped_score !== null && supplier.ppm_mapped_score !== undefined ? formatNumber(supplier.ppm_mapped_score, 1) : '-'}
            />
          </div>
        </CardContent>
      </Card>

      {/* Delivery Metrics - OTIF - CR-5765: Clickable rows */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            OTIF Delivery (PO Lines)
            <span className="text-xs text-muted-foreground font-normal ml-auto">Click values to drill down</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-border">
            <StatRow
              label="OTIF Score"
              value={otifPercent === null ? 'N/A' : `${formatNumber(otifPercent, 1)}%`}
              onClick={() => onOpenOtifDrilldown('total')}
            />
            <StatRow
              label="OTIF Lines"
              value={formatNumber(supplier.otif_count)}
              onClick={() => onOpenOtifDrilldown('positive')}
            />
            <StatRow
              label="Total PO Lines"
              value={formatNumber(supplier.po_line_count)}
              onClick={onNavigateToPOLines}
            />
            <StatRow
              label="Lines in Arrears"
              value={formatNumber(supplier.arrears_count)}
              onClick={() => onOpenOtifDrilldown('arrears')}
            />
            <StatRow
              label="Qty Difference"
              value={formatNumber(supplier.qty_diff)}
            />
            <StatRow
              label="OTIF Mapped Score"
              value={supplier.otif_mapped_score !== null && supplier.otif_mapped_score !== undefined ? formatNumber(supplier.otif_mapped_score, 1) : '-'}
            />
          </div>
        </CardContent>
      </Card>

      {/* Delivery Metrics - OTD (GRN based) - CR-5765: Clickable rows */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            OTD Delivery (GRN)
            <span className="text-xs text-muted-foreground font-normal ml-auto">Click values to drill down</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-border">
            <StatRow
              label="OTD Rate"
              value={`${formatNumber(otdPercent, 2)}%`}
              onClick={() => onOpenOtdDrilldown('total')}
            />
            <StatRow
              label="Total GRNs"
              value={formatNumber(supplier.grn_count)}
              onClick={() => onOpenOtdDrilldown('total')}
            />
            <StatRow
              label="On-Time"
              value={formatNumber(supplier.ontime_count)}
              onClick={() => onOpenOtdDrilldown('on_time')}
            />
            <StatRow
              label="Late"
              value={formatNumber(supplier.late_count)}
              onClick={() => onOpenOtdDrilldown('late')}
            />
            <StatRow
              label="Early"
              value={formatNumber(supplier.early_count)}
              onClick={() => onOpenOtdDrilldown('early')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Cost & Order Statistics - CR-5765: Clickable rows */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            Cost & Orders
            <span className="text-xs text-muted-foreground font-normal ml-auto">Click values to drill down</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-border">
            <StatRow
              label="Total Orders"
              value={formatNumber(supplier.total_orders)}
              onClick={() => onNavigateToPOHeaders('all')}
            />
            {/* CR-5766 fix: "Completed Orders" counts distinct POs with received lines,
                so navigate to PO Headers (not PO Lines) to match the order-level count.
                Use 'closed' status to show completed/closed orders. */}
            <StatRow
              label="Completed Orders"
              value={formatNumber(supplier.completed_orders)}
              onClick={() => onNavigateToPOHeaders('closed')}
            />
            {supplier.total_value && parseFloat(String(supplier.total_value)) > 0 && (
              <StatRow
                // FIX-14 (wave 3d): label the figure with the currency it is
                // actually IN. `supplier.currency` is the supplier's own
                // trading currency (Gould trades in USD); the API returns
                // this value in the viewer's display / reporting currency and
                // says so in `display_currency`. The card read
                // "Total Value (USD)" over a GBP number — the same class of
                // error as the sum this wave fixed, and it survived the sum
                // being corrected.
                label={`Total Value${supplier.display_currency ? ` (${supplier.display_currency})` : ''}`}
                value={formatNumber(supplier.total_value, 2)}
                onClick={() => onNavigateToPOHeaders('all')}
              />
            )}
            {supplier.reject_value && parseFloat(String(supplier.reject_value)) > 0 && (
              <StatRow
                // Same currency as Total Value directly above (round G20: one
                // of two adjacent money rows had been relabelled).
                label={`Reject Value${supplier.display_currency ? ` (${supplier.display_currency})` : ''}`}
                value={formatNumber(supplier.reject_value, 2)}
                onClick={() => onNavigateToNCRs()}
              />
            )}
            {/* CR-7069: Cost-of-quality hidden until proper implementation */}
            {supplier.carbon_emission !== undefined && supplier.carbon_emission !== null && (
              <StatRow
                label="CO₂ Emissions"
                value={`${formatNumber(supplier.carbon_emission, 2)} kg`}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
